import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { PricesGrid } from '@components/partials/card/prices-grid/prices-grid';
import { UiModule } from '@components/ui/ui-module';
import { CardBinder, CardCondition } from '@models/binder.model';
import { Card as CardM, CardPrice, CardSet, Rarity } from '@models/card.model';
import { BinderService } from '@services/binder.service';
import { Card } from '@services/card.service';
import { Global } from '@services/global.service';
import { Router } from '@services/route.service';
import { forkJoin, of, switchMap, tap } from 'rxjs';

@Component({
  selector: 'lgb-card-index',
  imports: [
    CommonModule,
    RouterModule,
    UiModule,
    PricesGrid
  ],
  templateUrl: './card-index.html',
  styleUrl: './card-index.css',
})
export class CardIndex {
  //services
  readonly router=inject(Router);
  readonly route=inject(ActivatedRoute);
  readonly cardS=inject(Card);
  readonly binderS=inject(BinderService);
  readonly global=inject(Global);

  //data
  readonly card=signal<CardM>(null);
  readonly inventory=signal<CardBinder[]>([]);
  readonly cardSet=signal<CardSet>(null);
  readonly backTo=signal<string>(null);
  readonly rarities=signal<Rarity[]>([]);
  readonly conditions=signal<CardCondition[]>([]);
  readonly prices=signal<CardPrice[]>([]);
  readonly name=computed(() => this.cardS.getNameFeat(this.card(), 'fr'));

  //ui
  readonly loading=signal(false);
  readonly length: number=5;
  readonly inventoryPage=signal<CardBinder[]>([]);
  readonly isReady=computed(() => (this.card()!=null && this.cardSet()!=null));

  constructor(){
    this.loading.set(true);
    this.route.queryParamMap
      .subscribe(params => this.backTo.set(params.get('backTo')));

    this.route.paramMap
      .pipe(
        switchMap(params => forkJoin({
          init: this.global.init(),
          details: this.cardS.getCardDetails(params.get('ident'))
        })),
        tap(result => {
          this.rarities.set(result.init.rarities);
          this.conditions.set(result.init.conditions);
          this.card.set(result.details.card);
          this.inventory.set(result.details.inventory);
        }),
        switchMap(() => {
          return forkJoin({
            cardSet: this.cardS.getCardSet(this.card().cardSet),
            prices: this.cardS.getPricesCardIndex(this.card().ident)
          });
        }),
      )
      .subscribe(result => {
        this.cardSet.set(result.cardSet);
        this.prices.set(result.prices);
        this.loading.set(false);
      });
  }

  goTo(){
    this.router.navigateByUrl(this.backTo());
  }

  getRarity(){
    return this.rarities().find(r => r.ref==this.card().rarity);
  }

  search(){
    return (q: string, start: number, length: number) => of(this.inventory().slice(start, start+length))
  }

  getCondition(condition: string){
    return this.conditions().find(c => c.ref==condition);
  }
}
